import { Tag } from "@phosphor-icons/react";
import { clsx } from "clsx";

import {
  DropdownMenu,
  DropdownMenuCheckbox,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FilterButton from "common/components/ui/FilterButton";
import { LABEL_COLORS } from "common/constants";
import useAllLabels from "common/datahooks/useAllLabels";

interface LabelSelectorProps {
  selectedLabelIds: string[];
  setSelectedLabelIds: (selectedLabelIds: string[]) => void;
}

export default function LabelSelector({
  selectedLabelIds,
  setSelectedLabelIds,
}: LabelSelectorProps) {
  const { allLabels, isLoadingAllLabels } = useAllLabels("workspace");
  const hasLabels = allLabels && allLabels.length > 0;

  function removeLabelId(labelId: string): void {
    setSelectedLabelIds(selectedLabelIds.filter((id) => id !== labelId));
  }

  function addLabelId(labelId: string): void {
    setSelectedLabelIds([...selectedLabelIds, labelId]);
  }

  let triggerLabel = "All labels";

  if (isLoadingAllLabels) {
    triggerLabel = "Loading labels...";
  } else if (!hasLabels) {
    triggerLabel = "No labels available";
  } else if (
    selectedLabelIds.length > 0 &&
    selectedLabelIds.length !== allLabels.length
  ) {
    triggerLabel =
      selectedLabelIds.length > 1
        ? `${selectedLabelIds.length} labels selected`
        : allLabels.find(({ id }) => id === selectedLabelIds[0])?.name;
  }

  const triggerIcon =
    hasLabels && selectedLabelIds.length === 1 ? (
      <div
        className={clsx(
          "size-[13px] shrink-0 rounded",
          LABEL_COLORS[
            allLabels.find(({ id }) => id === selectedLabelIds[0])?.color
          ].bgClassName,
        )}
      />
    ) : (
      <Tag />
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={!hasLabels || isLoadingAllLabels}>
        <FilterButton leftIcon={triggerIcon} isLoading={isLoadingAllLabels}>
          {triggerLabel}
        </FilterButton>
      </DropdownMenuTrigger>

      {hasLabels && (
        <DropdownMenuContent className="w-[--radix-popper-anchor-width] z-aboveDialog">
          <DropdownMenuList>
            <DropdownMenuItem
              onSelect={() => {
                setSelectedLabelIds([]);
              }}
            >
              All labels
            </DropdownMenuItem>

            {allLabels?.map(({ id, name, color }) => {
              const isLabelIdIncluded = selectedLabelIds.includes(id);

              return (
                <DropdownMenuItem
                  key={id}
                  onSelect={() => {
                    setSelectedLabelIds([id]);
                  }}
                >
                  <DropdownMenuCheckbox
                    isChecked={isLabelIdIncluded}
                    onCheckedChange={() => {
                      if (isLabelIdIncluded) {
                        removeLabelId(id);
                        return;
                      }

                      addLabelId(id);
                    }}
                  />

                  <div
                    className={clsx(
                      "size-3.5 shrink-0 rounded",
                      LABEL_COLORS[color].bgClassName,
                    )}
                  />
                  {name}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuList>
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
}
